/**
 * Українські назви модулів і документних таблиць (Порція Виробництво.5,
 * 2026-05-22, UX γ).
 *
 * Призначення — український label замість raw `module-code` / `doc.table`
 * у 4 точках UI:
 *  1. RoleEditorDialog — групування permissions по модулях.
 *  2. BusinessTemplateEditorDialog — чекбокси enabled_modules.
 *  3. ConfirmModuleNarrowingModal — попередження «X документів буде
 *     приховано у модулі Y» з людськими назвами.
 *  4. (опц.) sidebar group labels — вже українські у sidebarItems.ts.
 *
 * Узгоджено з:
 *  - `frontend/src/layouts/sidebarItems.ts` (поле `module` у link items)
 *  - `backend/src/app/services/business_template_service.py` MODULE_TO_DOC_TABLES
 */

export const MODULE_NAMES_UK: Record<string, string> = {
  refs: 'Довідники',
  sales: 'Продажі',
  purchases: 'Закупки',
  cash: 'Каса',
  bank: 'Банк',
  warehouse: 'Склад',
  tax: 'Податки',
  accounting: 'Бухгалтерія',
  reports: 'Звіти',
  assets: 'Основні засоби',
  fixed_assets: 'Основні засоби', // backend alias
  payroll: 'Кадри і ЗП',
  production: 'Виробництво',
  retail: 'Роздріб',
  // settlements — розділ «Розрахунки» у sidebar: акти звірки, довіреності,
  // інвентаризація розрахунків (НЕ договори — ті у картці контрагента).
  settlements: 'Розрахунки з контрагентами',
  // recognition — оголошений як ModuleCode, але у sidebar на нього не
  // прив'язано жодного пункту (модуль-привид). Назву не задаємо.
  returnable_packaging: 'Облік тари',  // Порція C, 2026-06-07
  // ч.1083: групи прав у редакторі ролей, що не є модулями меню.
  migration: 'Міграція з 1С',
  import: 'Імпорт довідників',
  admin: 'Адміністрування',
  documents: 'Документи',
  tenant: 'Акаунт',
  recognition: 'Розпізнавання документів',
  // ч.1098: права на використання агента (витрачають кредити акаунта).
  ai: 'Oblik+/AI',
  edo: 'ЕДО',
};

/**
 * Отримати українську назву модуля. Якщо код невідомий — повертає сам код
 * (fallback, щоб не загубити інформацію).
 */
export function moduleName(code: string): string {
  return MODULE_NAMES_UK[code] ?? code;
}

/**
 * Українські назви документних doc.* таблиць — для відображення у
 * confirmation modal «N документів у таблиці X буде приховано».
 *
 * Узгоджено з MODULE_TO_DOC_TABLES у backend сервісі.
 */
export const DOC_TABLE_NAMES_UK: Record<string, string> = {
  // production
  'doc.nomenclature_specifications': 'Специфікації',
  'doc.production_reports': 'Звіти виробництва за зміну',
  'doc.product_assemblies': 'Комплектація',
  'doc.product_disassemblies': 'Розукомплектація',
  'doc.material_transfers_for_processing': 'Передача в переробку',
  'doc.processing_receipts': 'Надходження з переробки',
  'doc.material_receipts_for_processing': 'Прийняття чужої сировини',
  'doc.processing_services_sales': 'Послуги переробки (продаж)',
  'doc.production_service_acts': 'Акти виробничих послуг',
  'doc.wip_inventory': 'Інвентаризація НЗВ',
  // sales
  'doc.goods_sales': 'Документ реалізації',
  'doc.customer_orders': 'Замовлення покупців',
  'doc.goods_returns_from_customer': 'Повернення від покупця',
  // purchases
  'doc.goods_receipts': 'Прибуткові накладні',
  'doc.supplier_orders': 'Замовлення постачальникам',
  'doc.customs_declarations': 'ВМД (імпорт)',
  'doc.additional_expenses': 'Додаткові витрати',
  'doc.goods_returns_to_supplier': 'Повернення постачальнику',
  // warehouse
  'doc.goods_transfers': 'Переміщення товарів',
  'doc.inventory_counts': 'Інвентаризація',
  'doc.goods_recognitions': 'Оприбуткування лишків',
  'doc.goods_writeoffs': 'Списання товарів',
  // bank/cash
  'doc.bank_receipts': 'Банк (надходження)',
  'doc.bank_payments': 'Банк (списання)',
  'doc.cash_receipts': 'Каса (надходження)',
  'doc.cash_payments': 'Каса (видача)',
  'doc.advance_reports': 'Авансові звіти',
  // tax
  'doc.tax_invoices_outgoing': 'ПН/РК (вихідні)',
  'doc.tax_invoices_incoming': 'ПН/РК (вхідні)',
  'tax.tax_invoices_outgoing': 'ПН/РК (вихідні)',
  'tax.tax_invoices_incoming': 'ПН/РК (вхідні)',
  // accounting
  'doc.manual_operations': 'Ручні операції',
  'doc.debt_adjustments': 'Взаємозалік',
  'doc.doubtful_debt_reserves': 'Резерв сумнівних',
  'doc.fx_revaluations': 'Переоцінка валюти',
  // payroll
  'doc.hire_orders': 'Прийом на роботу',
  'doc.staff_transfers': 'Кадрові переміщення',
  'doc.dismissal_orders': 'Звільнення',
  'doc.absence_orders': 'Тривала відсутність',
  'doc.timesheets': 'Табель робочого часу',
  'doc.payroll_documents': 'Документи ЗП',
  // fixed_assets
  'doc.fixed_asset_acceptances': 'Прийняття ОЗ',
  'doc.fixed_asset_disposals': 'Вибуття ОЗ',
  'doc.fixed_asset_modernizations': 'Модернізація ОЗ',
  // retail (Порція Роздріб.5a, 2026-05-23)
  'doc.retail_sales': 'Чеки роздрібного продажу',
  'doc.retail_returns': 'Повернення (роздріб)',
  'doc.cash_shift_open': 'Відкриття зміни',
  'doc.cash_shift_close': 'Закриття зміни (Z-звіт)',
  'doc.customer_card_issue': 'Видача карток',
  'doc.loyalty_transactions': 'Бонусні транзакції',
  'doc.price_change': 'Зміна цін',
  'doc.online_store_order': 'Замовлення з інтернет-магазину',
  // 2026-08-09: повне покриття для журналів (аудит/видалені/дт-кт).
  'doc.advance_payment_tax_registration': 'Реєстрація авансової ПН',
  'doc.asset_commissioning': 'Введення в експлуатацію',
  'doc.asset_internal_transfer': 'Внутрішнє переміщення активів',
  'doc.asset_write_off': 'Списання активів',
  'doc.average_earnings_compensations': 'Компенсації середнього заробітку',
  'doc.civil_law_contracts': 'Договори ЦПУ',
  'doc.commission_goods_transfer': 'Передача на комісію',
  'doc.commission_sale': 'Продаж комісіонером',
  'doc.commission_settlement_report': 'Звіт комісіонера',
  'doc.currency_purchase_sale': 'Купівля-продаж валюти',
  'doc.daily_timesheets': 'Щоденні табелі',
  'doc.depreciation_calculation': 'Нарахування амортизації',
  'doc.documents_journal': 'Журнал документів',
  'doc.dps_requests': 'Запити до ДПС',
  'doc.employee_loan_contracts': 'Договори позики працівникам',
  'doc.expected_vs_confirmed_vat_adjustments': 'Коригування очікуваного/підтвердженого ПДВ',
  'doc.fixed_asset_inventory': 'Інвентаризація ОЗ',
  'doc.fixed_asset_modernization': 'Модернізація ОЗ',
  'doc.fixed_asset_sale': 'Продаж ОЗ',
  'doc.fop_self_esv_payments': 'ЄСВ ФОП за себе',
  'doc.fop_self_wz_payments': 'ВЗ ФОП за себе',
  'doc.fop_zso_quarterly_calculations': 'Квартальні розрахунки ФОП (ЗСО)',
  'doc.fund_receipts': 'Надходження від фондів',
  'doc.hire_notifications': 'Повідомлення про прийом',
  'doc.profit_tax_declarations': 'Декларація з податку на прибуток',
  'doc.material_receipt_for_processing': 'Прийняття сировини в переробку',
  'doc.material_requisitions': 'Вимоги-накладні',
  'doc.material_transfer_for_processing': 'Передача в переробку',
  'doc.month_close_runs': 'Закриття місяця',
  'doc.opening_balances': 'Вступні залишки',
  'doc.payment_orders': 'Платіжні ордери',
  'doc.payroll_payments': 'Виплати зарплати',
  'doc.payrolls': 'Нарахування зарплати',
  'doc.planned_accrual_orders': 'Накази планових нарахувань',
  'doc.powers_of_attorney': 'Довіреності',
  'doc.proportional_vat_coefficient': 'Коефіцієнт пропорційного ПДВ',
  'doc.proportional_vat_recalculation': 'Перерахунок пропорційного ПДВ',
  'doc.reconciliation_acts': 'Акти звірки',
  'doc.service_acts': 'Акти наданих послуг',
  'doc.settlement_docs': 'Документи розрахунків',
  'doc.settlement_inventories': 'Інвентаризації розрахунків',
  'doc.sick_leave_calculations': 'Розрахунки лікарняних',
  'doc.sick_leaves': 'Лікарняні',
  'doc.single_tax_calculations': 'Розрахунки єдиного податку',
  'doc.staff_schedules': 'Штатні розклади',
  'doc.tare_return': 'Повернення тари',
  'doc.tare_transfer': 'Передача тари',
  'doc.tax_invoice_appendix_1': 'Додаток 1 до ПН',
  'doc.tax_invoice_appendix_2': 'Додаток 2 до ПН',
  'doc.tax_purpose_adjustments_inventory': 'Зміна податкового призначення (запаси)',
  'doc.tax_purpose_adjustments_tzr': 'Зміна податкового призначення (ТЗР)',
  'doc.tax_remittances': 'Сплата податків ЗП',
  'doc.tax_reports': 'Податкові звіти',
  'doc.tnn': 'ТТН',
  'doc.vacation_calculations': 'Розрахунки відпускних',
  'doc.vacation_compensations': 'Компенсації відпусток',
  'doc.vacation_orders': 'Накази на відпустку',
  'doc.vacation_schedules': 'Графіки відпусток',
  'doc.vat_declarations': 'Декларації з ПДВ',
  'doc.single_tax_declarations': 'Декларації єдинника',
  'doc.financial_statements': 'Фінансова звітність',
  'doc.disability_contribution_reports': 'Звіти про внесок (особи з інвалідністю)',
  'doc.stat_reports': 'Статистичні звіти до Держстату (1-опт, 10-зез)',
  'doc.stat_submissions': 'Подання до Держстату',
  'doc.stat_messages': 'Квитанції Держстату',
  'doc.warehouse_orders': 'Складські ордери',
};

export function docTableName(table: string): string {
  return DOC_TABLE_NAMES_UK[table] ?? table;
}
